import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Lock, Crown, Star, Sparkles, Eye, ChevronRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import MatchCelebration from "@/components/MatchCelebration";
import { getRecoveryTimeLabel, formatLocation } from "@/utils/recoveryTerminology";

const LikesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [likesData, setLikesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);

  useEffect(() => {
    fetchLikes();
  }, []);

  const fetchLikes = async () => {
    try {
      const response = await api.getWhoLikedMe();
      console.log('[LikesPage] Who liked me response:', {
        count: response.data.count,
        user_id: response.data.user_id,
        likes: response.data.likes?.length || 0
      });
      setLikesData(response.data);
    } catch (error) {
      console.error("Failed to load likes:", error.response?.data || error.message);
      toast.error("Failed to load likes");
    } finally {
      setLoading(false);
    }
  };

  const handleLikeBack = async (likerUserId, likerData) => {
    setActionLoading(likerUserId);
    try {
      const response = await api.likeBack(likerUserId);
      
      if (response.data.is_mutual_match) {
        setCelebrationData({
          matchedUserId: likerUserId,
          matchedUserPhoto: response.data.matched_user_photo || likerData?.photos?.[0],
          matchedUserName: response.data.matched_user_name || likerData?.name,
          compatibilityScore: response.data.compatibility_score,
        });
        setShowCelebration(true);
      } else {
        toast.success("Interest expressed!");
      }
      
      fetchLikes();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Upgrade to Recovery+ to like back!");
        navigate("/subscription");
      } else {
        toast.error(error.response?.data?.detail || "Something went wrong");
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleCelebrationClose = () => {
    setShowCelebration(false);
    setCelebrationData(null);
  };

  const getLastActiveLabel = (lastActive) => {
    if (!lastActive) return null;
    try {
      const lastActiveDate = new Date(lastActive);
      const now = new Date();
      const diffMs = now - lastActiveDate;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 5) return { text: 'Active now', isOnline: true };
      if (diffMins < 60) return { text: `${diffMins}m ago`, isOnline: false };
      if (diffHours < 24) return { text: `${diffHours}h ago`, isOnline: false };
      if (diffDays === 1) return { text: 'Yesterday', isOnline: false };
      if (diffDays < 7) return { text: `${diffDays}d ago`, isOnline: false };
      return { text: '7d+ ago', isOnline: false };
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FAB710] border-t-transparent mb-4"></div>
        <p className="text-gray-600">Loading likes...</p>
      </div>
    );
  }

  const isPremium = likesData?.is_premium;
  const likesCount = likesData?.count || 0;
  const likes = likesData?.likes || [];

  return (
    <div className="min-h-screen bg-gray-100 pb-24" data-testid="likes-page">
      {/* Match Celebration Modal */}
      <MatchCelebration
        isOpen={showCelebration}
        onClose={handleCelebrationClose}
        currentUserPhoto={user?.photos?.[0]}
        matchedUserPhoto={celebrationData?.matchedUserPhoto}
        matchedUserName={celebrationData?.matchedUserName}
        compatibilityScore={celebrationData?.compatibilityScore}
        onStartChat={() => {
          if (celebrationData?.matchedUserId) {
            navigate(`/chat/${celebrationData.matchedUserId}`);
          }
          handleCelebrationClose();
        }}
      />

      <div className="lg:max-w-6xl lg:mx-auto lg:px-8 lg:pt-8">
      {/* Themed Header - Like Mobile */}
      <div className="relative bg-gradient-to-br from-[#1C3A7A] via-[#0d1f3c] to-[#061022] pt-10 pb-8 px-4 rounded-b-[30px] lg:rounded-[30px] overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-white/[0.08]" />
        <div className="absolute bottom-4 -left-6 w-20 h-20 rounded-full bg-white/[0.05]" />

        {/* Centered Header Content */}
        <div className="flex flex-col items-center">
          {/* Icon */}
          <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#FAB710] to-[#FFD966] flex items-center justify-center mb-3 shadow-lg">
            <Heart className="w-7 h-7 text-white fill-white" />
          </div>
          
          <h1 className="text-2xl font-bold text-white drop-shadow-md">
            Likes You
          </h1>
          <p className="text-white/80 text-sm mt-1">
            {likesCount === 0 
              ? "No new admirers yet" 
              : `${likesCount} ${likesCount === 1 ? "person is" : "people are"} interested in you`}
          </p>

          {/* Count Badge */}
          {likesCount > 0 && (
            <div className="flex items-center gap-1.5 bg-[#FAB710] px-4 py-1.5 rounded-full mt-3">
              <Heart className="w-4 h-4 text-[#1C3A7A] fill-[#1C3A7A]" />
              <span className="text-[#1C3A7A] font-bold text-sm">{likesCount}</span>
            </div>
          )}
        </div>
      </div>

      {/* Premium Banner - Floating Card */}
      {isPremium && (
        <div className="px-4 -mt-4">
          <div className="bg-white rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FAB710]/20 flex items-center justify-center">
              <Crown className="w-5 h-5 text-[#FAB710]" />
            </div>
            <p className="flex-1 text-sm text-gray-700 font-medium">
              Recovery+ access - You can see and match with everyone who likes you!
            </p>
            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
              <span className="text-white text-xs">✓</span>
            </div>
          </div>
        </div>
      )}

      {/* Tip Card for non-premium users with likes */}
      {!isPremium && likesCount > 0 && (
        <div className="px-4 mt-4">
          <div className="bg-[#FAB710]/10 border border-[#FAB710]/30 rounded-2xl px-4 py-3 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#FAB710]" />
            <p className="flex-1 text-sm text-gray-700 font-medium">
              Upgrade to see who likes you and skip the wait!
            </p>
          </div>
        </div>
      )}

      <div className="px-4 mt-4">
        {likesCount === 0 ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#FAB710]/30 to-[#FAB710]/10 flex items-center justify-center mb-6">
              <Heart className="w-12 h-12 text-[#FAB710]" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No likes yet</h2>
            <p className="text-gray-500 text-center max-w-xs mb-6">
              Keep your profile active and check your daily matches to get more likes!
            </p>
            <Button
              onClick={() => navigate("/matches")}
              className="bg-[#1C3A7A] hover:bg-[#152d5e] text-white px-6"
            >
              View Daily Matches
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        ) : !isPremium ? (
          /* Blurred Grid for Free Users */
          <div className="relative">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-5 opacity-60">
              {likes.slice(0, 6).map((like, index) => (
                <div key={like.user_id || index} className="bg-white rounded-2xl overflow-hidden shadow-lg">
                  <div className="relative aspect-[3/4]">
                    {like.photos?.[0] ? (
                      <img
                        src={like.photos[0]}
                        alt={like.name}
                        className="w-full h-full object-cover blur-lg"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1C3A7A] to-[#0d1f3c]" />
                    )}
                    
                    {/* Lock Badge */}
                    <div className="absolute top-2 right-2 bg-white/20 p-2 rounded-full">
                      <Lock className="w-4 h-4 text-white" />
                    </div>
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    
                    {/* User Info */}
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="text-white font-bold blur-sm">{like.name || '???'}, {like.age || '??'}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Upgrade Overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-white/30 backdrop-blur-sm rounded-2xl">
              <div className="bg-white rounded-3xl p-6 shadow-xl max-w-sm mx-4 text-center border border-[#FAB710]/30">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#FAB710] to-amber-500 flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1C3A7A] mb-2">
                  {likesCount} {likesCount === 1 ? 'person likes' : 'people like'} you!
                </h3>
                <p className="text-gray-600 mb-4">
                  Upgrade to Recovery+ to see who they are and instantly match with them.
                </p>
                <Button
                  onClick={() => navigate("/subscription")}
                  className="w-full bg-gradient-to-r from-[#FAB710] to-amber-500 hover:from-[#e8a50f] hover:to-amber-600 text-white py-3"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Unlock for $9.99/month
                </Button>
                <p className="text-xs text-gray-400 mt-3">
                  Recovery+ includes unlimited messaging & 10 daily matches
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Full Grid for Premium Users */
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-5">
            {likes.map((like) => {
              const lastActiveInfo = getLastActiveLabel(like.last_active);
              const isLoading = actionLoading === like.user_id;
              
              return (
                <div key={like.user_id} className="bg-white rounded-2xl overflow-hidden shadow-lg">
                  <div className="relative aspect-[3/4]">
                    {like.photos?.[0] ? (
                      <img
                        src={like.photos[0]}
                        alt={like.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1C3A7A] to-[#0d1f3c] flex items-center justify-center">
                        <span className="text-5xl font-bold text-white/50">
                          {like.name?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    
                    {/* Last Active Badge - Top Right */}
                    {lastActiveInfo && (
                      <div className={`absolute top-2 right-2 px-2 py-1 rounded-lg flex items-center gap-1 ${lastActiveInfo.isOnline ? 'bg-green-500' : 'bg-black/50'}`}>
                        {lastActiveInfo.isOnline && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                        <span className="text-[10px] text-white font-medium">{lastActiveInfo.text}</span>
                      </div>
                    )}

                    {/* Compatibility Badge - Top Left */}
                    {like.compatibility_score > 0 && (
                      <div className="absolute top-2 left-2 bg-white/95 px-2 py-1 rounded-lg flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-[#FAB710]" />
                        <span className="text-[10px] font-bold text-[#1C3A7A]">{like.compatibility_score}%</span>
                      </div>
                    )}
                    
                    {/* User Info - Bottom */}
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="text-white font-bold text-base">
                        {like.name}, {like.age || '??'}
                      </h3>
                      {like.location && (
                        <p className="text-white/80 text-xs mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {formatLocation(like.location)}
                        </p>
                      )}
                      {like.days_clean > 0 && (
                        <div className="inline-block bg-green-500/90 px-2 py-0.5 rounded-full mt-1">
                          <span className="text-[10px] text-white font-semibold">
                            {getRecoveryTimeLabel(like.days_clean, like.recovery_terminology)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Like Back Button */}
                  <button
                    onClick={() => handleLikeBack(like.user_id, like)}
                    disabled={isLoading}
                    className="w-full py-3 flex items-center justify-center gap-2 bg-green-500 text-white font-bold hover:bg-green-600 transition disabled:opacity-50"
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Heart className="w-4 h-4 fill-white" />
                        <span>Like Back</span>
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default LikesPage;
